/**
 * Recommendation Service
 *
 * Core service for fetching product recommendations as a discovery mechanism.
 * Uses SearchService in read-only mode without live-search state machine.
 * Implements seed-based discovery with deduplication and caching.
 */

import { SearchService } from "../../services/searchService";
import { productNormalizer } from "../normalizers/productNormalizer";
import { productVariantsGrouper } from "../groupers/productVariantsGrouper";
import { ProductResult } from "../types/ProductResult";
import { isBitrixMode, buildBitrixApiUrl } from "../../utils/bitrixApiUrl";

import {
  RecommendationConfig,
  DEFAULT_RECOMMENDATION_CONFIG,
} from "./types";
import { getSeedQueries } from "./recommendationSource";
import { getCachedProducts, saveCache, clearCache } from "./recommendationStorage";

/**
 * Recommendation Service implementation
 */
export class RecommendationService {
  private searchService: SearchService;
  private config: RecommendationConfig;

  /**
   * Create a new RecommendationService instance
   *
   * @param searchService SearchService instance (used read-only)
   * @param config Configuration for recommendation generation
   */
  constructor(searchService: SearchService, config: Partial<RecommendationConfig> = {}) {
    this.searchService = searchService;
    this.config = { ...DEFAULT_RECOMMENDATION_CONFIG, ...config };
  }

  /**
   * Get product recommendations
   *
   * Flow (Bitrix mode):
   * 1. Fetch from Bitrix recommendations endpoint by KODTOVARA1S codes (no cache check)
   * 2. If empty/error → fallback to seed-based discovery
   * 3. Normalize results using productNormalizer
   * 4. Group product variants
   * 5. Return ProductResult[]
   *
   * Flow (dev mode):
   * 1. Check cache (memory → localStorage)
   * 2. Get seed queries (personalized → fallback)
   * 3. Execute discovery queries via SearchService
   * 4. Normalize results using searchMapper
   * 5. Deduplicate and limit products
   * 6. Cache results
   * 7. Return ProductResult[]
   *
   * @returns Promise resolving to array of product recommendations
   */
  async getRecommendations(): Promise<ProductResult[]> {
    try {
      if (isBitrixMode()) {
        // Bitrix production mode: always fetch fresh data, no cache check
        console.log("[RecommendationService] Bitrix mode: fetching fresh recommendations");
        return await this.getBitrixRecommendations();
      } else {
        // Dev mode: check cache first, then seed-based discovery
        const cachedProducts = getCachedProducts();
        if (cachedProducts && cachedProducts.length > 0) {
          console.log("[RecommendationService] Dev mode: using cached recommendations");
          return cachedProducts;
        }
        return await this.getSeedBasedRecommendations();
      }
    } catch (error) {
      console.error("[RecommendationService] Failed to generate recommendations:", error);
      // Graceful degradation: return empty array on error
      return [];
    }
  }

  /**
   * Get recommendations from Bitrix endpoint by KODTOVARA1S codes
   *
   * If Bitrix endpoint returns empty results or error, falls back to seed-based discovery.
   *
   * @returns Promise resolving to array of product recommendations
   */
  private async getBitrixRecommendations(): Promise<ProductResult[]> {
    try {
      const url = buildBitrixApiUrl("/api/recommendations");

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        // Коды товаров задаются в админке Bitrix (Option recommended_product_codes);
        // endpoint сам подставляет настроенный список, если codes не передан.
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        console.warn(
          `[RecommendationService] Bitrix endpoint returned ${response.status}, falling back to seed queries`
        );
        return await this.getSeedBasedRecommendations();
      }

      const data = await response.json();

      // Normalize hits to ProductResult
      const hits = data.hits || [];
      const normalizedProducts: (ProductResult | null)[] = hits.map((hit: any) =>
        productNormalizer(hit)
      );

      // Filter out null results
      const validProducts = normalizedProducts.filter(
        (product): product is ProductResult => product !== null
      );

      // If no products from Bitrix, fallback to seed queries
      if (validProducts.length === 0) {
        console.warn(
          "[RecommendationService] Bitrix returned no products, falling back to seed queries"
        );
        return await this.getSeedBasedRecommendations();
      }

      // Group product variants
      const groupedProducts = productVariantsGrouper(validProducts);

      console.log(
        `[RecommendationService] Got ${groupedProducts.length} recommendations from Bitrix`
      );

      return groupedProducts;
    } catch (error) {
      console.warn(
        "[RecommendationService] Bitrix request failed, falling back to seed queries:",
        error
      );
      return await this.getSeedBasedRecommendations();
    }
  }

  /**
   * Get recommendations using seed-based discovery (legacy/fallback)
   *
   * @returns Promise resolving to array of product recommendations
   */
  private async getSeedBasedRecommendations(): Promise<ProductResult[]> {
    try {
      // 2. Get seed queries
      const seedQueries = getSeedQueries(this.config.maxSeedQueries);
      if (seedQueries.length === 0) {
        console.warn("[RecommendationService] No seed queries available");
        return [];
      }

      console.log(`[RecommendationService] Using ${seedQueries.length} seed queries`);

      // 3. Execute discovery queries
      const allProducts = await this.executeDiscoveryQueries(seedQueries);

      // 4. Deduplicate and limit
      const deduplicatedProducts = this.deduplicateProducts(allProducts);
      const finalProducts = this.limitProducts(deduplicatedProducts, this.config.maxTotalProducts);

      console.log(`[RecommendationService] Generated ${finalProducts.length} recommendations`);

      // 5. Cache results
      if (finalProducts.length > 0) {
        saveCache(finalProducts, this.config.cacheTTL);
      }

      return finalProducts;
    } catch (error) {
      console.error("[RecommendationService] Seed-based recommendations failed:", error);
      return [];
    }
  }

  /**
   * Execute discovery queries using SearchService
   *
   * @param seedQueries Array of seed queries to execute
   * @returns Promise resolving to array of products from all queries
   */
  private async executeDiscoveryQueries(
    seedQueries: Array<{ query: string }>
  ): Promise<ProductResult[]> {
    const allProducts: ProductResult[] = [];

    // Execute queries sequentially to avoid overwhelming the service
    for (const seed of seedQueries) {
      try {
        const products = await this.executeDiscoveryQuery(seed.query);
        allProducts.push(...products);

        // Optional: small delay between queries
        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (error) {
        console.warn(
          `[RecommendationService] Failed to execute seed query "${seed.query}":`,
          error
        );
        // Continue with other queries
      }
    }

    return allProducts;
  }

  /**
   * Execute a single discovery query
   *
   * @param query Search query string
   * @returns Promise resolving to array of normalized products
   */
  private async executeDiscoveryQuery(query: string): Promise<ProductResult[]> {
    try {
      // Use SearchService in read-only mode
      const response = await this.searchService.search(query, {
        limit: this.config.maxProductsPerQuery,
      });

      // Normalize each result using productNormalizer
      // The SearchService preserves original hit data in result.metadata
      const normalizedProducts: (ProductResult | null)[] = response.results.map((result) => {
        // Use metadata if available (contains original hit), otherwise use result itself
        const hit = result.metadata || result;
        return productNormalizer(hit);
      });

      // Filter out null results (invalid hits)
      const validProducts = normalizedProducts.filter(
        (product): product is ProductResult => product !== null
      );

      // Group product variants
      const groupedProducts = productVariantsGrouper(validProducts);

      return groupedProducts;
    } catch (error) {
      console.error(`[RecommendationService] Search query failed for "${query}":`, error);
      throw error;
    }
  }

  /**
   * Deduplicate products by ID
   *
   * @param products Array of products to deduplicate
   * @returns Deduplicated array
   */
  private deduplicateProducts(products: ProductResult[]): ProductResult[] {
    const seenIds = new Set<string | number>();
    const deduplicated: ProductResult[] = [];

    for (const product of products) {
      if (!seenIds.has(product.id)) {
        seenIds.add(product.id);
        deduplicated.push(product);
      }
    }

    console.log(
      `[RecommendationService] Deduplicated ${products.length} → ${deduplicated.length} products`
    );
    return deduplicated;
  }

  /**
   * Limit products array to specified maximum
   *
   * @param products Array of products
   * @param limit Maximum number of products to return
   * @returns Limited array (first N products)
   */
  private limitProducts(products: ProductResult[], limit: number): ProductResult[] {
    return products.slice(0, limit);
  }

  /**
   * Clear recommendation cache
   *
   * Useful for forcing a refresh or debugging
   */
  clearCache(): void {
    clearCache();
  }

  /**
   * Update service configuration
   *
   * @param newConfig Partial configuration to merge
   */
  updateConfig(newConfig: Partial<RecommendationConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get current configuration
   */
  getConfig(): RecommendationConfig {
    return { ...this.config };
  }
}
